/**
 * AutoLink · Scans text for any registered Federation entity and renders it
 * with a tappable link to the canonical destination.
 *
 * Usage:
 *   <AutoLink text={message.content} />
 *
 *   The component returns a fragment of mixed text and styled <a>/<button>
 *   elements. Preserves `whitespace-pre-wrap` of the parent — newlines stay.
 *
 * Categories the registry routes:
 *   persona  → fires `nextxus:openchat` event (opens AIChatPopup)
 *   product  → SPA navigate to /redeem with the node id prefilled
 *   doctrine → opens markdown URL in a new tab
 *   page     → SPA navigate via React Router
 *
 * Visual: each link adopts the entity's `glow` color from the registry.
 */
import React from "react";
import { Link } from "react-router-dom";
import { getCompiledRegistry } from "@/lib/federationRegistry";

const glowClass = (g) => `text-glow-${g || "cyan"}`;

function personaLink(entry, displayText, idx) {
  return (
    <button
      key={`auto-${idx}-${entry.match}`}
      type="button"
      data-no-aichat
      data-testid={`autolink-persona-${entry.target}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(
          new CustomEvent("nextxus:openchat", { detail: { persona: entry.target } })
        );
      }}
      title={entry.desc || `Open ${entry.match} chat`}
      className={`${glowClass(entry.glow)} underline decoration-dotted underline-offset-2 hover:decoration-solid focus:outline-none focus:ring-1 focus:ring-current bg-transparent border-0 p-0 cursor-pointer font-mono`}
      style={{ display: "inline" }}
    >
      {displayText}
    </button>
  );
}

function productLink(entry, displayText, idx) {
  // Send to /redeem with node id prefilled in URL hash so the form picks it up
  return (
    <Link
      key={`auto-${idx}-${entry.match}`}
      to={`/redeem?node=${encodeURIComponent(entry.target)}`}
      data-testid={`autolink-product-${entry.target}`}
      title={entry.desc || `Redeem ${entry.match}`}
      className={`${glowClass(entry.glow)} underline decoration-dotted underline-offset-2 hover:decoration-solid`}
    >
      {displayText}
    </Link>
  );
}

function doctrineLink(entry, displayText, idx) {
  return (
    <a
      key={`auto-${idx}-${entry.match}`}
      href={entry.target}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`autolink-doctrine-${entry.match.replace(/\s+/g, "_")}`}
      title={entry.desc || `Open ${entry.match} doctrine`}
      className={`${glowClass(entry.glow)} underline decoration-dotted underline-offset-2 hover:decoration-solid`}
    >
      {displayText}&nbsp;↗
    </a>
  );
}

function pageLink(entry, displayText, idx) {
  return (
    <Link
      key={`auto-${idx}-${entry.match}`}
      to={entry.target}
      data-testid={`autolink-page-${entry.target.replace(/[^a-z0-9]/gi, "_")}`}
      title={entry.desc || `Go to ${entry.match}`}
      className={`${glowClass(entry.glow)} underline decoration-dotted underline-offset-2 hover:decoration-solid`}
    >
      {displayText}
    </Link>
  );
}

function buildLink(entry, displayText, idx) {
  switch (entry.kind) {
    case "persona":  return personaLink(entry, displayText, idx);
    case "product":  return productLink(entry, displayText, idx);
    case "doctrine": return doctrineLink(entry, displayText, idx);
    case "page":     return pageLink(entry, displayText, idx);
    default:         return displayText;
  }
}

export default function AutoLink({ text, className = "" }) {
  if (!text || typeof text !== "string") return text;

  const { regex, lookup } = getCompiledRegistry();
  // We need a fresh regex stateful per call (lastIndex)
  const localRx = new RegExp(regex.source, "gi");
  const out = [];
  let last = 0;
  let m;
  let idx = 0;

  while ((m = localRx.exec(text)) !== null) {
    const matchedText = m[1];
    const entry = lookup[matchedText.toLowerCase()];
    if (!entry) continue;

    // Push preceding plain text
    if (m.index > last) {
      out.push(text.slice(last, m.index));
    }
    out.push(buildLink(entry, matchedText, idx));
    idx += 1;
    last = m.index + matchedText.length;

    // Avoid zero-length infinite loop
    if (m.index === localRx.lastIndex) localRx.lastIndex++;
  }
  if (last < text.length) out.push(text.slice(last));

  return <span className={className}>{out}</span>;
}
